import { HudDrawer, useHudDrawerSideLabel } from '@sorrem-client/ui/components';
import { Button, HudPanel, Icon } from '@sorrem-client/ui/components';
import type { CrucibleMember, HudEdge, VeilSaturationBandDefinition } from '@sorrem/shared';
import { useEffect, useRef } from 'react';
import { useCopyInviteCode, useCopyInviteLink } from '../use-invite-link';
import styles from './CruciblePanel.module.scss';

interface CruciblePanelProps {
    /** Slides the panel in from its edge; closed, only its side label shows. */
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    /** The screen edge it slides off: the side of the commission's HUD area, whose place it takes. */
    readonly edge: HudEdge;
    readonly saturation: VeilSaturationBandDefinition;
    readonly members: readonly CrucibleMember[];
    /** Fewest members the run starts with (#206). */
    readonly minPlayers: number;
    /** The viewer's own membership, for whether they're ready; null while it hasn't arrived. */
    readonly self: CrucibleMember | null;
    /** The run has started and the host is launching. */
    readonly isStarting: boolean;
    /** Accepted a direct-entry commission: the page enters on its own, so there is nothing to ready up for. */
    readonly isDirectEntry: boolean;
    /** Whole seconds left on the ready-up countdown, or null when none is running. */
    readonly countdownSecondsRemaining: number | null;
    /** The run's models are loaded here; until then the viewer can't ready up, so the run never starts before they are. */
    readonly isRunLoaded: boolean;
    /** Why the server refused the last ready change, or null. */
    readonly readyError: string | null;
    /** The code friends type to join, with its link (#216); null when nobody else can join (solo, started, direct entry). */
    readonly invite: { readonly code: string; readonly link: string } | null;
    /** What veil-touched sense at its threshold (#225); null for none. */
    readonly backstory: string | null;
}

/**
 * The gathering page's Crucible panel, where the run's commission panel will be: the
 * crucible's Veil Saturation and modifiers and how many are ready (who is
 * gathered, and the viewer's Ready toggle, are on the party panel). Once
 * everyone is ready the countdown stands over the scene's entrance
 * (EntryCountdown); the footer only says so, and announces the seconds to
 * screen readers.
 */
export function CruciblePanel({
    open,
    onOpenChange,
    edge,
    saturation,
    members,
    minPlayers,
    self,
    isStarting,
    isDirectEntry,
    countdownSecondsRemaining,
    isRunLoaded,
    readyError,
    invite,
    backstory,
}: CruciblePanelProps): JSX.Element {
    const contentRef = useRef<HTMLDivElement>(null);
    const copyInviteLink = useCopyInviteLink();
    const copyInviteCode = useCopyInviteCode();
    const sideLabel = useHudDrawerSideLabel(edge, 'Crucible');
    const isCountingDown = !isStarting && !isDirectEntry && countdownSecondsRemaining !== null && countdownSecondsRemaining > 0;

    // A closed panel is slid off screen; keep its controls out of the Tab order.
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.inert = !open;
        }
    }, [open]);

    const isSelfReady = self?.isReady === true;
    const readyCount = members.filter((member) => member.isReady === true).length;

    return (
        <HudDrawer edge={edge} open={open} className={styles.crucibleDrawer}>
            <HudPanel
                title='Crucible'
                className={styles.cruciblePanel}
                {...sideLabel}
                onSideLabelClick={() => onOpenChange(!open)}
                footerClassName={styles.footer}
                footer={(
                    <div className={styles.enter}>
                        <p className={styles.status}>
                            {!isRunLoaded && !isSelfReady && !isStarting && !isDirectEntry
                                ? 'Preparing the Crucible...'
                                : resolveStatusLine({ isStarting, isDirectEntry, countdownSecondsRemaining, readyCount, memberCount: members.length, minPlayers })}
                        </p>
                        {/* The countdown itself stands over the scene's entrance (EntryCountdown); screen readers hear it here. */}
                        <p className={styles.srOnly} role='status'>
                            {isCountingDown ? `Entering the Crucible in ${countdownSecondsRemaining}` : ''}
                        </p>
                        {readyError !== null && <p className={styles.error} role='alert'>{readyError}</p>}
                        {isSelfReady && !isStarting && countdownSecondsRemaining === null && (
                            <p className={styles.hint}>Gear and crystals are locked while you are ready.</p>
                        )}
                        {!isSelfReady && isRunLoaded && !isDirectEntry && !isStarting && self !== null && (
                            <p className={styles.hint}>Click Ready? beside your name to ready up.</p>
                        )}
                    </div>
                )}
            >
                <div ref={contentRef} className={styles.content} aria-hidden={!open}>
                    {backstory !== null && <p className={styles.backstory}>{backstory}</p>}
                    <section>
                        <h3 className={styles.saturationName}>{saturation.displayName}</h3>
                        <ul className={styles.modifierList}>
                            {saturation.modifiers.map((modifier) => (
                                <li key={modifier}>{modifier}</li>
                            ))}
                        </ul>
                    </section>
                    {invite !== null && (
                        <section className={styles.invite}>
                            <p className={styles.inviteLabel}>Invite code</p>
                            <div className={styles.inviteCodeRow}>
                                <p className={styles.inviteCode}>{invite.code}</p>
                                <Button variant="link" onClick={() => void copyInviteCode(invite.code)} aria-label="Copy invite code">
                                    <Icon icon="copy" size="small" tooltip="Copy invite code" />
                                </Button>
                            </div>
                            <Button variant="secondary" className={styles.inviteButton} onClick={() => void copyInviteLink(invite.link)}>
                                Copy invite link
                            </Button>
                        </section>
                    )}
                </div>
            </HudPanel>
        </HudDrawer>
    );
}

interface StatusLineInput {
    readonly isStarting: boolean;
    readonly isDirectEntry: boolean;
    readonly countdownSecondsRemaining: number | null;
    readonly readyCount: number;
    readonly memberCount: number;
    readonly minPlayers: number;
}

function resolveStatusLine({ isStarting, isDirectEntry, countdownSecondsRemaining, readyCount, memberCount, minPlayers }: StatusLineInput): string {
    if (isStarting || isDirectEntry) {
        return 'Opening the rift...';
    }
    if (countdownSecondsRemaining !== null) {
        return countdownSecondsRemaining > 0 ? 'Everyone is ready.' : 'Opening the rift...';
    }
    // The run won't start until the party reaches its minimum size (#206).
    const missing = minPlayers - memberCount;
    return missing > 0
        ? `${readyCount} of ${memberCount} ready - waiting for ${missing} more`
        : `${readyCount} of ${memberCount} ready`;
}
